/**
 * Pure utility functions with no i18n dependency.
 *
 * Rules:
 *  - No imports of `useI18n` or passing `t` as a parameter.
 *  - Every function is independently testable without Vue context.
 *  - For currency formatting use the `useCurrency` composable in components.
 *  - For status labels/colors use the `useItemStatus` composable in components.
 */

/**
 * Format a date value into { date, time, full } using the current app locale.
 *
 * @param {string|Date} date
 * @param {string}      locale - BCP 47 tag from useI18n().locale.value ('ar'|'en')
 * @param {object}      options
 * @returns {{ date: string, time: string, full: Date } | null}
 */
export function formatDateTime(date, locale = 'ar', options = {}) {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;

  return {
    date: dateObj.toLocaleDateString(locale, {
      day:   'numeric',
      month: 'short',
      ...options.dateOptions,
    }),
    time: dateObj.toLocaleTimeString(locale, {
      hour:   '2-digit',
      minute: '2-digit',
      ...options.timeOptions,
    }),
    full: dateObj,
  };
}

/**
 * Bucket a timestamp into a calendar-relative recency band for friendly
 * "when did this happen" labels. Compares CALENDAR days in local time, so an
 * order at 11pm and "now" at 1am the next day correctly reads as 'yesterday'
 * (not "2 hours ago"). Pure: pass `now` in tests for determinism.
 *
 * @param {string|Date} date
 * @param {Date}        [now] - reference instant (defaults to current time)
 * @returns {'today'|'yesterday'|'older'|null} null for missing/invalid input
 */
export function relativeDayBucket(date, now = new Date()) {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDelta = Math.round((startOfDay(now) - startOfDay(dateObj)) / 86_400_000);

  if (dayDelta <= 0) return 'today'; // same day (future clamps to today)
  if (dayDelta === 1) return 'yesterday';
  return 'older';
}
